import { createContext, useState, ReactNode, FC, useCallback, useEffect, useMemo } from 'react';
import {
  PartnerLandingSectionItem,
  PartnerLandingSectionLayout,
} from 'lib.store/src/type-partner/landings/models';
import {
  ButtonType,
  CardType,
  PartnerLandingSectionOptions,
} from 'lib.context/src/models/landingPage';
import { generateUniqueStringId } from 'lib.shared/src/utils';
import { showApiError } from 'lib.shared/src/handleApiError';
import { message, notification } from 'antd';
import {
  usePatchPartnerLandingByIdMutation,
  usePostPartnerCreateLandingMutation,
  usePublishLandingMutation,
  useGetPartnerLandingByIdQuery,
  useGetLandingsLayoutsSectionsQuery,
} from 'lib.store/src/type-partner/landings/landings.api';
import { useNavigate, useParams } from 'react-router-dom';
import {
  changeVideoUrl,
  replaceMediaQueriesBack,
  setNewEditorStyles,
} from 'lib.context/src/utils/helpers.ts';
import { OptionIds } from 'lib.context/src/constants/constantslp';
import { useAppSelector } from 'lib.hooks/src/redux';
import { MAX_UPDATED_SECTIONS_COUNT } from 'lib.context/src/constants/constants.tsx';
import { addCardsStyles } from 'lib.context/src/utils/helpers';
import { PUBLISH_WITHOUT_MODERATION_TRANSIT } from 'lib.shared/src/constants';
import { usePageContext } from 'lib.context/src/PageContext';

export interface Option {
  name: OptionIds;
  value:
    | string
    | string[]
    | boolean
    | null
    | { card: CardType[] }
    | { image: string }[]
    | { button: ButtonType[] };
  key?: string;
}

export interface ImageReplaceInfo {
  componentName: string;
  optionKey: string;
  value: string;
  index?: number;
}

type ChangesArrayType = {
  type: 'project_title' | 'sections';
  id: string;
  data?: { id?: string; layout: PartnerLandingSectionLayout }[];
  title: string;
};

interface LandingEditorContextType {
  landingFetchedId: string;
  setLandingFetchedId: (id: string) => void;

  selectedSectionIndex: number | null;
  setSelectedSectionIndex: (idx: number | null) => void;

  editorSections: { id?: string; layout: PartnerLandingSectionLayout }[];
  setEditorSections: (
    sections: { id?: string; layout: PartnerLandingSectionLayout; hidden?: boolean }[],
  ) => void;

  layoutSettingsData: PartnerLandingSectionLayout | null;
  setLayoutSettingsData: (layout: PartnerLandingSectionLayout | null) => void;

  clickedSectionId: string | null | undefined;
  setClickedSectionId: (id: string | null | undefined) => void;

  updateSectionOptionById: (data: Option) => void;
  updateSectionHtmlById: (data: { id: string; html: string }) => void;
  updateEditorSectionImageOption: (sectionId: string, info: ImageReplaceInfo) => void;
  onSaveSectionSettingsClick: () => void;

  onSectionAddToList: (layout: PartnerLandingSectionLayout, idx?: number) => void;
  onDeleteSectionFromList: (id: string) => void;

  fetchedSections: PartnerLandingSectionItem[];
  setFetchedSections: (sections: PartnerLandingSectionItem[]) => void;

  isAddSectionModalOpen: boolean;
  setIsAddSectionModalOpen: (isOpen: boolean) => void;

  onPublishClick: (statusId: number, transit?: string, transition?: number) => void;

  landingTitle: string;
  setLandingTitle: (title: string) => void;
  landingUrl: string;
  setLandingUrl: (url: string) => void;

  updateLandingHandler: ({
    sections,
    isCampaignCtx,
  }: {
    sections?: { id?: string; layout: PartnerLandingSectionLayout }[];
    isCampaignCtx?: boolean;
  }) => void;
  createLandingHandler: ({
    sections,
    isCampaignCtx,
  }: {
    sections?: { id?: string; layout: PartnerLandingSectionLayout }[];
    isCampaignCtx?: boolean;
  }) => void;
  isLoading: boolean;
  isPublishedModalOpen: boolean;
  onClosePublishedModal: () => void;
  isPublishLoading: boolean;
  landingErrors: { [key: string]: string[] };
  setLandingErrors: (errors: { [key: string]: string[] }) => void;
  onPreviewClick: () => void;

  // AI EDITOR
  isAiLoading: boolean;
  setAiLoading: (isOpen: boolean) => void;

  isPublishDropdownOpen: boolean;
  setIsPublishDropdownOpen: (isOpen: boolean) => void;

  isLandingSavedModalOpen: boolean;
  setLandingSavedModalOpen: (isOpen: boolean) => void;

  getLayoutsByLayoutName: (layoutName: string) => {
    layouts: PartnerLandingSectionLayout[];
    selectedLayout: PartnerLandingSectionLayout;
  };

  isSaveDisabled: boolean;

  handleUndoClick: () => void;
  handleRedoClick: () => void;
  setLastUpdatedSectionIdState: (id: string | null) => void;
  isUndoBtnDisabled: boolean;
  isRedoBtnDisabled: boolean;
  currentChangesIdState: string | null;
  undoRedoId: string | null;
  setUndoRedoId: (id: string | null) => void;

  isFillUrlModalOpen: boolean;
  setIsFillUrlModalOpen: (isOpen: boolean) => void;
}

export const LandingEditorContext = createContext<LandingEditorContextType>({
  // EDITOR
  landingFetchedId: '',
  setLandingFetchedId: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  landingTitle: '',
  setLandingTitle: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  setLandingErrors: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  landingErrors: {},
  landingUrl: '',
  setLandingUrl: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  fetchedSections: [],
  setFetchedSections: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  // ACTIONS TO STATE
  editorSections: [],
  setEditorSections: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  layoutSettingsData: null,
  setLayoutSettingsData: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  selectedSectionIndex: null,
  setSelectedSectionIndex: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  clickedSectionId: null,
  setClickedSectionId: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  onSectionAddToList: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  onDeleteSectionFromList: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  // ACTIONS TO BE
  onPublishClick: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  updateLandingHandler: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  createLandingHandler: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  updateSectionOptionById: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  updateSectionHtmlById: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  updateEditorSectionImageOption: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  onSaveSectionSettingsClick: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  onPreviewClick: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  // LOADERS
  isAiLoading: false,
  setAiLoading: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  isLoading: false,
  isPublishLoading: false,

  // MODALS DRAWERS
  isPublishedModalOpen: false,
  onClosePublishedModal: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  isAddSectionModalOpen: false,
  setIsAddSectionModalOpen: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  isPublishDropdownOpen: false,
  setIsPublishDropdownOpen: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  isLandingSavedModalOpen: false,
  setLandingSavedModalOpen: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  getLayoutsByLayoutName: () => ({
    layouts: [],
    selectedLayout: null as unknown as PartnerLandingSectionLayout,
  }), // eslint-disable-line @typescript-eslint/no-empty-function

  isSaveDisabled: true,

  handleUndoClick: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  handleRedoClick: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  setLastUpdatedSectionIdState: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  isUndoBtnDisabled: true,
  isRedoBtnDisabled: true,
  currentChangesIdState: null,
  undoRedoId: null,
  setUndoRedoId: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function

  isFillUrlModalOpen: false,
  setIsFillUrlModalOpen: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
});

interface LandingEditorContextProviderProps {
  children: ReactNode;
}

export const LandingEditorContextProvider: FC<LandingEditorContextProviderProps> = ({
  children,
}) => {
  const { landingId = '', type: _type = '' } = useParams();
  const navigate = useNavigate();
  const { campaignEditorsData: _campaignEditorState, temporaryEditorData } = useAppSelector(
    (state) => state.partnerCampaigns,
  );
  const { filters: pageFilters } = usePageContext();
  const contextEventId = pageFilters.eventIds.length > 0 ? pageFilters.eventIds[0] : '';

  const [landingFetchedId, setLandingFetchedId] = useState('');
  const [clickedSectionId, setClickedSectionId] = useState<string | null | undefined>(null);
  const [fetchedSections, setFetchedSections] = useState<PartnerLandingSectionItem[]>([]);
  const [editorSections, setEditorSections] = useState<
    { id?: string; layout: PartnerLandingSectionLayout }[]
  >([]);
  const [layoutSettingsData, setLayoutSettingsData] = useState<PartnerLandingSectionLayout | null>(
    null,
  );
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number | null>(null);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [landingErrors, setLandingErrors] = useState<{ [key: string]: string[] }>({});
  const [landingTitle, setLandingTitle] = useState('Project title');
  const [landingUrl, setLandingUrl] = useState('');
  const [isPublishLoading, setPublishLoading] = useState(false);
  const [isPublishedModalOpen, setIsPublishedModalOpen] = useState(false);
  const [isPublishDropdownOpen, setIsPublishDropdownOpen] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isLandingSavedModalOpen, setLandingSavedModalOpen] = useState(false);
  const [isAiLoading, setAiLoading] = useState(false);
  const [changesArray, setChangesArray] = useState<ChangesArrayType[]>([]);
  const [currentChangesIdState, setCurrentChangesIdState] = useState<string | null>(null);
  const [lastUpdatedSectionIdState, setLastUpdatedSectionIdState] = useState<string | null>(null);
  const [undoRedoId, setUndoRedoId] = useState<string | null>(null);
  const [isFillUrlModalOpen, setIsFillUrlModalOpen] = useState(false);

  const [publishLanding] = usePublishLandingMutation();
  const [updateLanding] = usePatchPartnerLandingByIdMutation();
  const [createLanding] = usePostPartnerCreateLandingMutation();
  const { data: fetchedLandingData } = useGetPartnerLandingByIdQuery(
    { landingId },
    { skip: !landingId },
  );
  // eventId: from landing data (edit), campaign context (create from campaign), or PageContext (create standalone)
  const eventId =
    fetchedLandingData?.data?.event?.id?.toString() ||
    temporaryEditorData?.event_id?.toString() ||
    contextEventId;
  const { data: layoutsResponse } = useGetLandingsLayoutsSectionsQuery();

  useEffect(() => {
    if (isAddSectionModalOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [isAddSectionModalOpen]);

  useEffect(() => {
    if (landingErrors?.['url.path']) {
      setIsPublishDropdownOpen(true);
    }
  }, [landingErrors]);

  useEffect(() => {
    if (!layoutSettingsData) {
      setIsSaveDisabled(true);
    }
  }, [layoutSettingsData]);

  const updateSectionOptionById = (option: Option) => {
    if (isSaveDisabled) {
      setIsSaveDisabled(false);
    }
    const prevOptions = layoutSettingsData?.options?.[option.name] || {};
    let newOptionValue;
    if (option.name === OptionIds.CardsComponent || option.name === OptionIds.ButtonsComponent) {
      newOptionValue = option.value;
    } else {
      newOptionValue = {
        ...prevOptions,
        [option.key || 'value']: option.value,
      };
    }
    if (
      option.name === OptionIds.BackgroundComponent ||
      option.name === OptionIds.FullBackgroundImageColorComponent
    ) {
      if (option.key === 'backgroundColor') {
        newOptionValue = {
          ...prevOptions,
          backgroundImage: 'none',
          [option.key]: option.value,
        };
      }
      if (option.key === 'backgroundImage') {
        newOptionValue = {
          ...prevOptions,
          backgroundColor: '',
          [option.key]: option.value,
        };
      }
    }
    const updatedOptions = {
      ...(layoutSettingsData?.options || {}),
      [option.name]: newOptionValue,
    };
    const updatedLayoutSettingsData = {
      ...layoutSettingsData,
      options: updatedOptions,
    };

    setNewEditorStyles({ clickedSectionId, option });

    setLayoutSettingsData(updatedLayoutSettingsData as PartnerLandingSectionLayout);
  };

  const updateSectionHtmlById = useCallback(({ id, html }: { id: string; html: string }) => {
    const newId = generateUniqueStringId();
    setLastUpdatedSectionIdState(newId);
    setEditorSections((prevSections) => {
      return prevSections.map((section) => {
        if (section.id === id) {
          return {
            ...section,
            layout: {
              ...section.layout,
              html: replaceMediaQueriesBack(html),
            },
          };
        }
        return section;
      });
    });
  }, []);

  const updateEditorSectionImageOption = useCallback(
    (sectionId: string, info: ImageReplaceInfo) => {
      if (isSaveDisabled) {
        setIsSaveDisabled(false);
      }
      setEditorSections((prevSections) =>
        prevSections.map((section) => {
          if (section.id !== sectionId) return section;
          const prevOptions = section.layout.options || {};
          const prevComponentOptions =
            (prevOptions as Record<string, Record<string, unknown>>)[info.componentName] || {};

          let newComponentOptions: Record<string, unknown>;
          const currentValue = prevComponentOptions[info.optionKey];
          if (info.index !== undefined) {
            const arr = [...((currentValue as { image: string }[]) || [])];
            arr[info.index] = { ...arr[info.index], image: info.value };
            newComponentOptions = { ...prevComponentOptions, [info.optionKey]: arr };
          } else if (Array.isArray(currentValue)) {
            // Safety: if current value is an array but no index provided, don't overwrite with string
            newComponentOptions = prevComponentOptions;
          } else {
            newComponentOptions = { ...prevComponentOptions, [info.optionKey]: info.value };
          }

          return {
            ...section,
            layout: {
              ...section.layout,
              options: {
                ...prevOptions,
                [info.componentName]: newComponentOptions,
              },
            },
          };
        }),
      );
    },
    [isSaveDisabled],
  );

  useEffect(() => {
    if (lastUpdatedSectionIdState) {
      setChangesArray((prev) => {
        const newChange: ChangesArrayType = {
          type: lastUpdatedSectionIdState.includes('project_title') ? 'project_title' : 'sections',
          id: lastUpdatedSectionIdState,
          data: editorSections,
          title: landingTitle,
        };
        const currentChangesItemIndex = prev.findIndex(({ id }) => currentChangesIdState === id);
        return [
          ...prev.slice(
            prev.length === MAX_UPDATED_SECTIONS_COUNT ? 1 : 0,
            currentChangesItemIndex + 1,
          ),
          newChange,
        ];
      });
      setLastUpdatedSectionIdState(null);
      setCurrentChangesIdState(lastUpdatedSectionIdState);
    }
  }, [editorSections, landingTitle, lastUpdatedSectionIdState, currentChangesIdState]);

  const onSectionAddToList = (layout: PartnerLandingSectionLayout, idx?: number) => {
    const newId = generateUniqueStringId();
    const newItem = { id: newId, layout };

    const sectionIndex = selectedSectionIndex ?? idx;

    let next;
    if (sectionIndex !== null && sectionIndex !== undefined) {
      next = [
        ...editorSections.slice(0, sectionIndex),
        newItem,
        ...editorSections.slice(sectionIndex),
      ];
    } else {
      next = [...editorSections, newItem];
    }

    setEditorSections(next);
    setLastUpdatedSectionIdState(newId);
    setIsAddSectionModalOpen(false);
    setSelectedSectionIndex(null);
    localStorage.setItem('newSectionIdAdded', newId);
  };

  const onDeleteSectionFromList = useCallback(
    (id: string) => {
      const updatedSections = editorSections.filter((section) => section.id !== id);
      setEditorSections(updatedSections);
    },
    [editorSections],
  );

  const publishHandler = async (
    statusId: number,
    transit: string | undefined,
    transition: number,
  ) => {
    setPublishLoading(true);
    try {
      const response = await publishLanding({ landingId, status: statusId, transition });
      if ('data' in response) {
        // void refetchLandingData();
        setPublishLoading(false);
        setIsFillUrlModalOpen(false);
        const normalizedTransit = transit?.toLowerCase() || '';
        const shouldOpenModerationModal =
          normalizedTransit.includes('publish') &&
          normalizedTransit !== PUBLISH_WITHOUT_MODERATION_TRANSIT.toLowerCase();
        if (shouldOpenModerationModal) {
          setIsPublishedModalOpen(true);
        }
        return notification.open({
          message: `The ${fetchedLandingData?.data?.title || ''} Landing Page has been successfully ${transit || ''}`,
          placement: 'bottomLeft',
          closeIcon: false,
          type: 'success',
        });
      }
      if ('error' in response) {
        setPublishLoading(false);
        return message.open({
          type: 'error',
          content: 'Page uploading error',
        });
      }
    } catch (error) {
      setPublishLoading(false);
      return message.open({
        type: 'error',
        content: 'Page uploading error',
      });
    }
  };

  const updateLandingHandler = async ({
    sections,
    isCampaignCtx,
    isShowNotification = true,
  }: {
    sections?: { id?: string; layout: PartnerLandingSectionLayout }[];
    isCampaignCtx?: boolean;
    isShowNotification?: boolean;
  }) => {
    const content = (sections || editorSections).map((section) => section.layout);
    const mappedContent = content.map((contentItem) => ({
      ...(contentItem || {}),
      html: contentItem.html,
    }));
    const payload = {
      landingId,
      eventId,
      body: {
        title: landingTitle,
        url: {
          path: landingUrl,
        },
        content: mappedContent,
      },
    };
    setLoading(true);
    try {
      // todo change response when it s ready
      const response = await updateLanding(payload);
      if ('data' in response) {
        setLoading(false);
        if (response?.data?.data?.id) {
          setLandingFetchedId(response.data.data.id);
          // return refetchLandingData()
        }
        setIsPublishDropdownOpen(false);
        setLayoutSettingsData(null);

        if (isCampaignCtx) {
          setLandingSavedModalOpen(true);
        }
        if (isShowNotification) {
          notification.open({
            message: 'Changes saved',
            placement: 'bottomLeft',
            closeIcon: false,
            type: 'success',
          });
        }
        return;
      }
      setLoading(false);
      showApiError(response, `Error ${landingId ? 'updating' : 'creating'} landing page`);
      const rawErrors = (response as { error?: { data?: { errors?: Record<string, string[]> } } })
        ?.error?.data?.errors;
      if (rawErrors) {
        setLandingErrors(rawErrors);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleUndoClick = useCallback(() => {
    if (currentChangesIdState) {
      const findIndex = changesArray.findIndex(({ id }) => id === currentChangesIdState);
      const prevItem = changesArray[findIndex - 1];
      if (!prevItem) {
        return;
      }
      setCurrentChangesIdState(prevItem.id);
      setUndoRedoId(prevItem.id);
      if (prevItem.type === 'sections' && prevItem.data) {
        setEditorSections(prevItem.data);
        setLandingTitle(prevItem.title);
      }
      if (prevItem.type === 'project_title' && typeof prevItem.title === 'string') {
        setLandingTitle(prevItem.title);
      }
    }
  }, [changesArray, currentChangesIdState]);

  const isUndoBtnDisabled = useMemo(() => {
    const findIndex = changesArray.findIndex(({ id }) => id === currentChangesIdState);
    const prevItem = changesArray[findIndex - 1];
    return !prevItem;
  }, [changesArray, currentChangesIdState]);

  const isRedoBtnDisabled = useMemo(() => {
    const findIndex = changesArray.findIndex(({ id }) => id === currentChangesIdState);
    const nextItem = changesArray[findIndex + 1];
    return !nextItem;
  }, [changesArray, currentChangesIdState]);

  const handleRedoClick = useCallback(() => {
    if (currentChangesIdState) {
      const findIndex = changesArray.findIndex(({ id }) => id === currentChangesIdState);
      const nextItem = changesArray[findIndex + 1];
      if (nextItem) {
        setCurrentChangesIdState(nextItem.id);
        setUndoRedoId(nextItem.id);
        if (nextItem.type === 'sections' && nextItem.data) {
          setEditorSections(nextItem.data);
          setLandingTitle(nextItem.title);
        }
        if (nextItem.type === 'project_title' && typeof nextItem.title === 'string') {
          setLandingTitle(nextItem.title);
        }
      }
    }
  }, [changesArray, currentChangesIdState]);

  const createLandingHandler = async ({
    sections,
    isCampaignCtx,
  }: {
    sections?: { id?: string; layout: PartnerLandingSectionLayout }[];
    isCampaignCtx?: boolean;
  }) => {
    setLoading(true);
    const content = (sections || editorSections).map((section) => section.layout);
    const payload = {
      eventId,
      body: {
        title: landingTitle,
        url: {
          path: landingUrl,
        },
        content,
      },
    };
    try {
      const response = await createLanding(payload);
      if ('data' in response) {
        setLoading(false);
        if (response?.data?.data?.id) {
          setLandingFetchedId(response.data.data.id);
          if (isCampaignCtx) {
            setLandingSavedModalOpen(true);
            setIsPublishDropdownOpen(false);
            setLayoutSettingsData(null);
          } else {
            navigate(`/landing-pages/${response.data.data.id}/edit`);
          }
        } else {
          setLoading(false);
          showApiError(response, 'Creating landing error');
          const rawErrors = (
            response as { error?: { data?: { errors?: Record<string, string[]> } } }
          )?.error?.data?.errors;
          if (rawErrors) {
            setLandingErrors(rawErrors);
          }
        }
      }
    } catch (error) {
      setLoading(false);
      // Handle error
      console.error('Error:', error);
    }
  };

  const onPreviewClick = () => {
    const page = fetchedLandingData?.data?.page;
    if (landingId && page) {
      window.open(page, '_blank');
    }
  };

  const onSaveSectionSettingsClick = async () => {
    setLoading(true);
    let newLayoutSettingsData = layoutSettingsData;
    const hasVideoUrl = layoutSettingsData?.options?.VideoComponent?.video;
    if (hasVideoUrl) {
      const fixedVideoUrl = changeVideoUrl(layoutSettingsData?.options?.VideoComponent?.video);
      if (fixedVideoUrl) {
        newLayoutSettingsData = {
          ...layoutSettingsData,
          options: {
            ...(layoutSettingsData?.options || {}),
            VideoComponent: {
              ...layoutSettingsData?.options?.VideoComponent,
              video: fixedVideoUrl,
            } as PartnerLandingSectionOptions['VideoComponent'],
          },
        };
      }
    }
    if (newLayoutSettingsData?.options?.CardsComponent?.card) {
      const newHtml = addCardsStyles(
        newLayoutSettingsData?.html,
        newLayoutSettingsData?.options?.CardsComponent?.card,
      );
      newLayoutSettingsData = {
        ...newLayoutSettingsData,
        html: newHtml,
      };
    }
    const updatedSections = editorSections.map((section) => {
      if (section.id === clickedSectionId) {
        return {
          ...section,
          layout: newLayoutSettingsData as PartnerLandingSectionLayout,
        };
      }
      return section;
    });
    setEditorSections(updatedSections);
    if (landingId) {
      await updateLandingHandler({ sections: updatedSections, isShowNotification: false });
    } else {
      await createLandingHandler({ sections: updatedSections });
    }
    setLoading(false);
  };

  const onPublishClick = async (statusId: number, transit?: string, transition?: number) => {
    if (transition === undefined) {
      return;
    }
    if (!landingUrl) {
      if (isFillUrlModalOpen) {
        setLandingErrors({
          'url.path': ['Page URL is required to publish the landing page'],
        });
      } else {
        setIsFillUrlModalOpen(true);
      }
      return;
    }
    try {
      if (landingId) {
        await updateLandingHandler({});
      } else {
        await createLandingHandler({});
      }
      await publishHandler(statusId, transit, transition);
    } catch (err) {
      // Handle error based on lpId condition
      console.error(`Error ${landingId ? 'updating' : 'creating'} landing:`, err);
      return message.open({
        type: 'error',
        content: `Error ${landingId ? 'updating' : 'creating'} landing page`,
      });
    }
  };

  const onClosePublishedModal = () => {
    setIsPublishedModalOpen(false);
  };

  const getLayoutsByLayoutName = (layoutName: string) => {
    const foundSection = layoutsResponse?.data?.find((section) => {
      return section.section && layoutName.includes(section.section);
    });
    return {
      layouts: foundSection?.layouts || [],
      selectedLayout: foundSection?.layouts?.find(({ name }) => name === layoutName),
    };
  };

  return (
    <LandingEditorContext.Provider
      value={{
        landingFetchedId,
        setLandingFetchedId,

        selectedSectionIndex,
        setSelectedSectionIndex,

        clickedSectionId,
        setClickedSectionId,
        fetchedSections,
        setFetchedSections,

        updateSectionOptionById,
        updateSectionHtmlById,
        updateEditorSectionImageOption,
        onSectionAddToList,
        onDeleteSectionFromList,

        editorSections,
        setEditorSections,
        layoutSettingsData,
        setLayoutSettingsData,

        isAddSectionModalOpen,
        setIsAddSectionModalOpen,

        landingTitle,
        setLandingTitle,
        landingUrl,
        setLandingUrl,

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        updateLandingHandler,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        createLandingHandler,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onPublishClick,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSaveSectionSettingsClick,
        onPreviewClick,

        isLoading,
        isPublishedModalOpen,
        onClosePublishedModal,
        isPublishLoading,
        landingErrors,
        setLandingErrors,

        isAiLoading,
        setAiLoading,
        isPublishDropdownOpen,
        setIsPublishDropdownOpen,
        getLayoutsByLayoutName,
        isLandingSavedModalOpen,
        setLandingSavedModalOpen,
        isSaveDisabled,

        handleUndoClick,
        handleRedoClick,
        isUndoBtnDisabled,
        isRedoBtnDisabled,
        setLastUpdatedSectionIdState,
        currentChangesIdState,
        undoRedoId,
        setUndoRedoId,
        isFillUrlModalOpen,
        setIsFillUrlModalOpen,
      }}
    >
      {children}
    </LandingEditorContext.Provider>
  );
};
